// Client-side consent checking utility for marketing tracking (opt-out system)
// This only affects Facebook/TikTok marketing pixels, not PostHog product analytics

type CookieConsent = {
  action?: string;
  categories?: string[];
};

/** Returns false if the browser sends a privacy signal (GPC or DNT). */
function hasPrivacySignal(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    navigator.globalPrivacyControl === true || navigator.doNotTrack === "1"
  );
}

/**
 * Reads consent from CookieScript's runtime API.
 * Returns true/false if a verdict is available, or null if the API is absent.
 */
function consentFromCookieScript(): boolean | null {
  if (typeof window === "undefined") return null;
  const state = window.CookieScript?.instance?.currentState?.();
  if (!state) return null;
  const isBlocked =
    state.targeting === false ||
    state.marketing === false ||
    state.advertisement === false;
  return !isBlocked;
}

function readConsentCookie(): CookieConsent | null {
  if (typeof document === "undefined") return null;
  const cookie = document.cookie
    .split(";")
    .find((c) => c.trim().startsWith("CookieScriptConsent="));
  if (!cookie) return null;
  try {
    return JSON.parse(decodeURIComponent(cookie.split("=")[1]));
  } catch {
    return null;
  }
}

/** Interprets a parsed CookieScript cookie. */
function consentFromCookie(consent: CookieConsent): boolean {
  if (consent.action === "reject") return false;
  const categories = consent.categories;
  if (!Array.isArray(categories)) return true;
  if (categories.length === 1 && categories.includes("necessary")) return false;
  return (
    categories.includes("targeting") ||
    categories.includes("marketing") ||
    categories.includes("advertisement")
  );
}

export function canTrackUser(): boolean {
  if (hasPrivacySignal()) return false;

  try {
    const liveVerdict = consentFromCookieScript();
    if (liveVerdict !== null) return liveVerdict;

    const cookie = readConsentCookie();
    if (cookie) return consentFromCookie(cookie);

    // Default to allow tracking (opt-out system)
    return true;
  } catch {
    return true;
  }
}

// Type declarations for CookieScript (for TypeScript)
declare global {
  interface Window {
    CookieScript?: {
      instance?: {
        currentState?: () => {
          targeting?: boolean;
          marketing?: boolean;
          advertisement?: boolean;
          necessary?: boolean;
          performance?: boolean;
        };
      };
    };
  }

  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}
